/**
 * Services component that fetches its own data.
 * Uses per-section API endpoints for better performance.
 */
import type { KillChainSelectDto, ServiceSubSelectDto } from '@@types/api.types';
import { AreaBarChartComponent } from '@components/shared/area-bar-chart.component';
import { AvatarItem } from '@components/shared/avatar-item.component';
import { BadgeList } from '@components/shared/badge-list.component';
import { CountryFlag } from '@components/shared/country-flag.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import {
  useMissionAssessmentCoreQueryOptions,
  useMissionAssessmentMetadataQueryOptions,
  useMissionAssessmentServicesQueryOptions,
} from '@queries/mission-assessments.query';
import { useQuery } from '@tanstack/react-query';
import { MILITARY_SERVICE_COUNTRY_TO_CODE } from '@utils/constants';
import { sectionProvenance } from '@utils/mission-assessment-provenance.util';
import type { FunctionComponent } from 'react';
import {
  buildCapabilityDefinitionByName,
  toCapabilityTooltipItems,
} from '../../mission-assessment/components/mission-assessment-capability-definition.util';
import { MissionAssessmentServicesSkeleton } from './mission-assessment-skeletons.component';
import { TechnologyMissionAssessmentSectionHeaderComponent } from './technology-mission-assessment-section-header.component';
import { TechnologyMissionAssessmentTagComponent } from './technology-mission-assessment-tag.component';

type TechnologyMissionAssessmentServicesProps = {
  missionAssessmentId: string;
};

const groupKillChainsByCategory = (
  killChains: KillChainSelectDto[],
): { category: string; items: KillChainSelectDto[] }[] => {
  const groups = new Map<string, KillChainSelectDto[]>();
  for (const kc of killChains) {
    const existing = groups.get(kc.category);
    if (existing) {
      existing.push(kc);
    } else {
      groups.set(kc.category, [kc]);
    }
  }
  return Array.from(groups, ([category, items]) => ({ category, items }));
};

const getServiceBadge = (service: ServiceSubSelectDto) => {
  const countryService = service.parentService?.name;
  if (!countryService) return undefined;

  const countryCode = MILITARY_SERVICE_COUNTRY_TO_CODE[countryService];
  if (!countryCode) return undefined;

  return <CountryFlag country={countryCode} />;
};

export const TechnologyMissionAssessmentServicesComponent: FunctionComponent<
  TechnologyMissionAssessmentServicesProps
> = (props) => {
  const { missionAssessmentId } = props;

  const {
    data: coreData,
    isLoading: isCoreLoading,
    error: coreError,
  } = useQuery(useMissionAssessmentCoreQueryOptions(missionAssessmentId));
  const {
    data: servicesData,
    isLoading: isServicesLoading,
    error: servicesError,
  } = useQuery(useMissionAssessmentServicesQueryOptions(missionAssessmentId));
  const {
    data: metadataData,
    isLoading: isMetadataLoading,
    error: metadataError,
  } = useQuery(useMissionAssessmentMetadataQueryOptions(missionAssessmentId));

  const isLoading = isCoreLoading || isServicesLoading || isMetadataLoading;

  if (isLoading) {
    return <MissionAssessmentServicesSkeleton />;
  }

  const taskRecommendations = servicesData?.taskRecommendations ?? [];
  const recommendedServices = servicesData?.recommendedServices ?? [];
  const communities = metadataData?.communities ?? [];
  const capabilities = metadataData?.capabilities ?? [];
  const killChains = metadataData?.killChains ?? [];

  const hasTaskRecommendations = taskRecommendations.length > 0;
  const hasServices = recommendedServices.length > 0;
  const hasCommunities = communities.length > 0;
  const hasCapabilities = capabilities.length > 0;
  const hasKillChains = killChains.length > 0;

  const itemValueRange =
    typeof coreData?.data?.minPhase === 'number' && typeof coreData?.data?.maxPhase === 'number'
      ? {
          max: coreData.data.maxPhase,
          min: coreData.data.minPhase,
        }
      : undefined;

  const sortedCapabilities = [...capabilities].sort((a, b) => a.name.localeCompare(b.name));
  const sortedCommunities = [...communities].sort((a, b) => a.name.localeCompare(b.name));
  const sortedServices = [...recommendedServices].sort((a, b) => a.service.name.localeCompare(b.service.name));
  const capabilityDefinitions = buildCapabilityDefinitionByName(metadataData?.capabilities ?? []);
  const capabilityTooltipItems = toCapabilityTooltipItems({
    definitions: capabilityDefinitions,
    items: sortedCapabilities,
  });

  return (
    <div className="space-y-6">
      {hasTaskRecommendations && (
        <TechnologyMissionAssessmentSectionHeaderComponent
          provenance={coreData?.provenance?.TASK_RECOMMENDATIONS}
          title="Task Recommendations"
        >
          <div className="space-y-4">
            {taskRecommendations.map((taskRecommendation) => (
              <div
                className="text-gray-700 border border-gray-200 rounded-md p-2"
                key={taskRecommendation.id}
              >
                <ExpandableText
                  maxLines={2}
                  text={taskRecommendation.description}
                />
              </div>
            ))}
          </div>
        </TechnologyMissionAssessmentSectionHeaderComponent>
      )}

      {hasServices && (
        <TechnologyMissionAssessmentSectionHeaderComponent
          provenance={sectionProvenance(coreData?.provenance, 'RECOMMENDED_SERVICES')}
          title="Recommended Services"
        >
          <div className="flex flex-col gap-y-5">
            {sortedServices.map(({ service, unit }, index) => (
              <div
                className="flex items-center"
                key={`${service.id}-${index}`}
              >
                <AvatarItem
                  altText={`${service.name} Icon`}
                  badge={getServiceBadge(service)}
                  className="items-center"
                  imageUrl={`/images/services/${service.slug}.png`}
                  initialText={service.name}
                  size="lg"
                  subtitle={unit}
                  title={service.name}
                />
              </div>
            ))}
          </div>
        </TechnologyMissionAssessmentSectionHeaderComponent>
      )}

      {hasCommunities && (
        <TechnologyMissionAssessmentSectionHeaderComponent
          provenance={sectionProvenance(coreData?.provenance, 'COMMUNITIES')}
          title="Functional Communities"
        >
          <div className="flex flex-wrap gap-2">
            {sortedCommunities.map((community) => (
              <TechnologyMissionAssessmentTagComponent
                key={community.id}
                text={community.name}
              />
            ))}
          </div>
        </TechnologyMissionAssessmentSectionHeaderComponent>
      )}

      {hasCapabilities && (
        <TechnologyMissionAssessmentSectionHeaderComponent
          provenance={sectionProvenance(coreData?.provenance, 'CAPABILITIES')}
          title="Joint Capabilities"
        >
          <BadgeList
            items={capabilityTooltipItems}
            limit={6}
            tooltipMode="icon"
          />
        </TechnologyMissionAssessmentSectionHeaderComponent>
      )}

      {hasKillChains && (
        <TechnologyMissionAssessmentSectionHeaderComponent
          provenance={sectionProvenance(coreData?.provenance, 'KILL_CHAINS')}
          title="Kill Chain Elements"
        >
          <div className="space-y-3">
            {groupKillChainsByCategory(killChains).map(({ category, items }) => (
              <div key={category}>
                <p className="text-xs font-medium text-gray-500 mb-1">{category}</p>
                <div className="flex flex-wrap gap-2">
                  {items.map((killChain, index) => (
                    <TechnologyMissionAssessmentTagComponent
                      key={`${killChain.id}-${index}`}
                      text={killChain.name}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TechnologyMissionAssessmentSectionHeaderComponent>
      )}

      {itemValueRange && (
        <TechnologyMissionAssessmentSectionHeaderComponent
          provenance={coreData?.provenance?.PHASES}
          title="Phases of Operation"
        >
          <AreaBarChartComponent
            customLabels={['Shape', 'Deter', 'Seize Initiative', 'Dominate', 'Stabilize', 'Enable Civil Authority']}
            items={[{ valueRange: itemValueRange }]}
            valueRange={{ max: 5, min: 0 }}
          />
        </TechnologyMissionAssessmentSectionHeaderComponent>
      )}
    </div>
  );
};
